import type { SpectralWorkspaceSession } from '../types';
import {
  computeSessionFullTractMetrics,
  type SessionFullTractMetrics,
} from './computeSessionFullTractMetrics';

const METRICS_STORAGE_KEY = 'spectral-workspace-tract-metrics-v1';
const MAX_PERSISTED_METRICS = 256;

const cache = new Map<string, SessionFullTractMetrics>();

type PersistedMetricsStore = Record<string, SessionFullTractMetrics>;

let persistTimer: ReturnType<typeof setTimeout> | null = null;

const hydrateMetricsFromStorage = () => {
  if (typeof sessionStorage === 'undefined') return;

  try {
    const raw = sessionStorage.getItem(METRICS_STORAGE_KEY);
    if (!raw) return;

    const parsed = JSON.parse(raw) as PersistedMetricsStore;
    Object.entries(parsed).forEach(([key, value]) => {
      cache.set(key, value);
    });
  } catch {
    // Ignore corrupt or unavailable storage.
  }
};

const schedulePersistMetrics = () => {
  if (typeof sessionStorage === 'undefined') return;

  if (persistTimer) {
    clearTimeout(persistTimer);
  }

  persistTimer = setTimeout(() => {
    persistTimer = null;

    try {
      const store: PersistedMetricsStore = {};
      let count = 0;

      for (const [key, value] of cache.entries()) {
        if (count >= MAX_PERSISTED_METRICS) break;
        store[key] = value;
        count += 1;
      }

      sessionStorage.setItem(METRICS_STORAGE_KEY, JSON.stringify(store));
    } catch {
      // Ignore quota errors.
    }
  }, 400);
};

hydrateMetricsFromStorage();

export const sessionMetricsFingerprint = (
  session: Pick<SpectralWorkspaceSession, 'sessionId' | 'events'>
) => {
  const events = session.events ?? [];
  const lastTimestamp =
    events.length > 0 ? (events[events.length - 1]?.timestamp ?? 0) : 0;

  return `${session.sessionId}:${events.length}:${lastTimestamp}`;
};

export const isSessionFullTractMetricsCached = (
  session: Pick<SpectralWorkspaceSession, 'sessionId' | 'events'>
) => cache.has(sessionMetricsFingerprint(session));

export const resolveSessionFullTractMetrics = (
  session: Pick<SpectralWorkspaceSession, 'sessionId' | 'events'>
): SessionFullTractMetrics => {
  const fingerprint = sessionMetricsFingerprint(session);
  const cached = cache.get(fingerprint);
  if (cached) return cached;

  const next = computeSessionFullTractMetrics(session);
  cache.set(fingerprint, next);
  schedulePersistMetrics();
  return next;
};

export const resolveSessionsFullTractMetrics = (
  sessions: Pick<SpectralWorkspaceSession, 'sessionId' | 'events'>[]
) => {
  let computedCount = 0;
  let cacheHits = 0;

  const metrics = sessions.map(session => {
    const fingerprint = sessionMetricsFingerprint(session);
    const cached = cache.get(fingerprint);
    if (cached) {
      cacheHits += 1;
      return cached;
    }

    computedCount += 1;
    const next = computeSessionFullTractMetrics(session);
    cache.set(fingerprint, next);
    return next;
  });

  if (computedCount > 0) {
    schedulePersistMetrics();
  }

  return { metrics, computedCount, cacheHits };
};

export const clearSessionFullTractMetricsCache = () => {
  cache.clear();

  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(METRICS_STORAGE_KEY);
    } catch {
      // Ignore storage errors.
    }
  }
};
